import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import config from '../../config';

export default function CreatorLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState('');
  const [registrationEnabled, setRegistrationEnabled] = useState(null);

  useEffect(() => {
    checkRegistrationStatus();
  }, []);

  const checkRegistrationStatus = async () => {
    try {
      const response = await fetch(`${config.API_URL}/settings/registration-enabled`);
      const data = await response.json();
      setRegistrationEnabled(data.registration_enabled);
    } catch (err) {
      console.error('Failed to check registration status:', err);
      // If we can't check, assume it's enabled to show the link
      setRegistrationEnabled(true);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLogging(true);

    try {
      const loginRes = await fetch(`${config.API_URL}/auth/jwt/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          username: formData.email,
          password: formData.password
        }),
      });

      if (!loginRes.ok) {
        const errorData = await loginRes.json();
        throw new Error(errorData.detail || 'Invalid email or password');
      }

      const loginData = await loginRes.json();

      // Store the JWT tokens (FastAPI Users returns access_token directly)
      localStorage.setItem('access_token', loginData.access_token);
      localStorage.setItem('token_type', loginData.token_type || 'bearer');

      // Get user data from the /users/me endpoint using the token
      const userRes = await fetch(`${config.API_URL}/users/me`, {
        headers: {
          'Authorization': `Bearer ${loginData.access_token}`
        }
      });

      if (userRes.ok) {
        const userData = await userRes.json();
        localStorage.setItem('user_email', userData.email);
        localStorage.setItem('user_name', `${userData.first_name} ${userData.last_name}`);
        localStorage.setItem('user_id', userData.id);
      }
      
      // Redirect to creator dashboard
      navigate('/creator-dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLogging(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-black text-white justify-center items-center p-4">
      <form onSubmit={handleLogin} className="bg-gray-800 p-8 rounded-lg w-full max-w-md border border-blue-500 space-y-4">
        <h2 className="text-2xl font-bold text-center text-white mb-6">Creator Login</h2>

        {error && (
          <div className="bg-red-900 border border-red-500 text-red-200 px-4 py-2 rounded">
            {error}
          </div>
        )}

        <input
          className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-400"
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <input
          className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-400"
          type="password"
          name="password"
          placeholder="Password"
          value={formData.password}
          onChange={handleChange}
          required
        />

        <button
          className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600 transition disabled:bg-gray-600"
          type="submit"
          disabled={logging}
        >
          {logging ? 'Logging in...' : 'Log In'}
        </button>

        <div className="space-y-2 text-center">
          <p className="text-sm">
            <Link to="/forgot-password" className="text-blue-400 hover:text-blue-300">
              Forgot your password?
            </Link>
          </p>
          {registrationEnabled === true ? (
            <p className="text-sm">
              Don't have an account?{' '}
              <Link to="/creator-signup" className="text-blue-400 hover:text-blue-300">
                Sign up
              </Link>
            </p>
          ) : registrationEnabled === false ? (
            <div className="bg-yellow-900 border border-yellow-500 text-yellow-200 px-3 py-2 rounded text-sm">
              <p className="font-medium">Account creation is currently disabled.</p>
              <p className="text-xs mt-1">New signups are temporarily unavailable.</p>
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
}